import React from 'react';
import { Trophy, Sparkles, ChevronRight } from 'lucide-react';
import { MILESTONES } from '../mock';
import { useCountry, useCtaHandler } from '../context/CountryContext';
import FadeInSection from './FadeInSection';
import SafeMotion from './SafeMotion';

const IconFor = ({ isFinal }) => (isFinal ? <Sparkles className="w-5 h-5" /> : <Trophy className="w-5 h-5" />);

export default function Rewards() {
  const { telegramUrl } = useCountry();
  const track = useCtaHandler();
  return (
    <section id="rewards" className="relative py-24 md:py-32">
      <div className="absolute inset-x-0 top-10 h-[420px] radial-glow-soft opacity-70 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6">
        <FadeInSection className="text-center max-w-3xl mx-auto">
          <h2 className="font-display font-extrabold text-white text-4xl md:text-[52px] leading-[1.05] tracking-tight">
            Tiered cash rewards.
            <br />
            <span className="text-orange-gradient">Live payouts every week.</span>
          </h2>
          <p className="mt-5 text-[#f2f2f2] text-[15px]">
            Every Silver milestone unlocks a real cash bonus. Track your journey from left to right, from Milestone 1 all the way to Bali.
          </p>
        </FadeInSection>

        {/* Milestone row with chevrons between */}
        <div className="mt-14 flex flex-col md:flex-row items-stretch gap-4 md:gap-2">
          {MILESTONES.map((m, i) => (
            <React.Fragment key={m.tag}>
              <SafeMotion
                initial={{ filter: 'blur(10px)', opacity: 0, y: 30 }}
                whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1, margin: '0px 0px -80px 0px' }}
                transition={{ duration: 0.7, ease: 'easeOut', delay: i * 0.15 }}
                className="card-dark p-6 md:p-6 flex-1 flex flex-col relative"
              >
                {/* Top row: icon + milestone label */}
                <div className="flex items-center justify-between">
                  <div className="icon-badge"><IconFor isFinal={m.isFinal} /></div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-[#f2f2f2]">{m.tag}</div>
                </div>

                {/* Divider */}
                <div className="mt-6 h-px bg-white/10" />

                {/* Silver Volume */}
                <div className="mt-5">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#f2f2f2]">Silver Volume</div>
                  <div className="font-display font-bold text-2xl md:text-[26px] text-white mt-2">{m.lots}</div>
                </div>

                {/* Divider */}
                <div className="mt-5 h-px bg-white/10" />

                {/* Your Reward */}
                <div className="mt-5">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#f2f2f2]">Your Reward</div>
                  <div className={`mt-2 font-semibold text-lg ${m.isFinal ? 'text-orange-gradient font-display uppercase' : 'text-white normal-case'}`} style={m.isFinal ? {} : { textTransform: 'none' }}>
                    {m.reward}
                  </div>
                  <div className="text-[#f2f2f2] text-[13px] mt-1" style={{ textTransform: 'none' }}>{m.note}</div>
                </div>

                {/* Divider */}
                <div className="mt-5 h-px bg-white/10" />

                {/* Cumulative + auto credited */}
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#f2f2f2]">Cumulative</div>
                    <div className="font-display font-bold text-xl text-orange-gradient mt-1">{m.cumulative}</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-[11.5px] text-[#f2f2f2]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a2f]" /> Auto credited
                  </div>
                </div>
              </SafeMotion>

              {/* Chevron between cards (desktop only) */}
              {i < MILESTONES.length - 1 && (
                <div className="hidden md:flex items-center justify-center px-1">
                  <ChevronRight className="w-6 h-6 text-[#ff7a2f]" strokeWidth={2.5} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <FadeInSection delay={0.1} className="mt-12 text-center">
          <a href="https://bali.mbealert.com/public-leaderboard" target="_blank" rel="noreferrer" onClick={() => track('rewards-leaderboard')} className="btn-outline-dark inline-flex items-center gap-2 px-6 py-3.5 text-[14.5px]">
            Join the Weekly Leaderboard
          </a>
        </FadeInSection>
      </div>
    </section>
  );
}
